import { bezierSpline, lineString } from '@turf/turf';
import type { BBox, Feature, GeoJsonProperties, Point } from 'geojson';
import pluralize from 'pluralize';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type Supercluster from 'supercluster';
import type { Place, Places } from '~/libs/dato/queries/places';
import type { AnyJourneyItemTransformed, StayTransformed } from '~/types/JourneyItem.types';
import type { PlaygroundPlace } from '~/types/Playground.types';
import { formatExactDate } from '~/utils/dateHelpers/dateHelpers';
import { isSleeperLeg } from '~/utils/transportModeHelper';
import { MAP_CONSTANTS } from './mapConstants';

/**
 * Unified type for map markers - contains only the essential data needed for map rendering
 * This type is used across different map components to ensure consistency
 */
export type MapOverviewItem = {
  /** Unique identifier for the map item */
  id: string;
  /** Display title for the location */
  title: string;
  /** Geographic coordinates for map positioning */
  geoCoordinates: { latitude: number; longitude: number };
  /** Type of journey item (Stay, Journey, or Stop) */
  type: 'Stay' | 'Journey' | 'Stop';
  /** Number of nights for stays */
  nights: number;
  /** Array of stay dates for locations with multiple visits */
  stayDates?: { startDate: string; endDate: string; nights: number }[];
  /** Position in the journey timeline */
  timelinePosition: 'start' | 'middle' | 'end';
  /** Start date of the item */
  startDate?: string;
  /** End date of the item */
  endDate?: string;
  /** Sleeper flag - don't display as pin on maps but display in timelines */
  isSleeper: boolean;
};

/**
 * Helper function to create stayDates for stays - used to display multiple stays on one pin
 *
 * @param itemTitle - The title of the stay location to find dates for
 * @param allStays - Array of all journey items to search through
 * @returns Array of stay date objects, sorted chronologically and deduplicated
 *
 * @example
 * ```typescript
 * const dates = getStayDates('Paris Hotel', journeyItems)
 * // Returns: [{ startDate: '2023-01-01', endDate: '2023-01-03', nights: 2 }]
 * ```
 */
export const getStayDates = (itemTitle: string, allStays: AnyJourneyItemTransformed[]) => {
  try {
    // Find all stays with the same title and same date range (to avoid duplicates)
    const allStaysWithSameTitle = allStays.filter(
      (stay) => stay.type === 'Stay' && 'title' in stay && stay.title === itemTitle
    );

    // Group by date range to avoid duplicates
    const uniqueStays = new Map<string, any>();
    allStaysWithSameTitle.forEach((stay) => {
      const dateKey = `${stay.startDate}-${stay.endDate}`;
      if (!uniqueStays.has(dateKey)) {
        uniqueStays.set(dateKey, stay);
      }
    });

    // Map to date objects and sort chronologically
    return Array.from(uniqueStays.values())
      .map((stay) => ({
        startDate: stay.startDate,
        endDate: stay.endDate,
        nights: 'nights' in stay ? stay.nights : 0
      }))
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  } catch (_error) {
    return [];
  }
};

/**
 * Gets the display title for a multi-night (or WALK) Journey leg shown mid-timeline
 */
function getMiddleJourneyTitle(modeOfTransports: string[] | undefined, nights: number | undefined, origin: string) {
  if (isSleeperLeg(modeOfTransports)) return 'Sleeper with cabin';
  if (modeOfTransports?.includes('WALK') && nights && nights >= 1) return 'Walk';
  return origin;
}

/**
 * Unified function to create map overview from journey items.
 * This replaces the duplicate getOverview functions in StadiaPlaygroundMap and JourneyMap
 *
 * @param items - Array of transformed journey items (Journey, Stay, or Stop)
 * @returns Array of MapOverviewItem objects ready for map rendering
 *
 * @example
 * ```typescript
 * const overview = getMapOverview(journeyItems)
 * // Returns: [{ id: '1', title: 'London', type: 'Journey', timelinePosition: 'start' }, ...]
 * ```
 */

export function getMapOverview(items: AnyJourneyItemTransformed[]): MapOverviewItem[] {
  if (!items || items.length === 0) return [];
  try {
    const first = items[0];
    const last = items[items.length - 1];
    const middleItems = items.slice(1, -1);

    // Create origin (start)
    const originTitle = first.type === 'Journey' ? first.origin : first.title;
    const getOrigin = (first: AnyJourneyItemTransformed): MapOverviewItem => {
      if (first.type === 'Stay') {
        return {
          id: first.id,
          title: originTitle,
          geoCoordinates: first.geoCoordinates,
          type: 'Stay' as const,
          nights: first.nights,
          timelinePosition: 'start' as const,
          stayDates: getStayDates(originTitle ?? '', items),
          startDate: first.startDate,
          endDate: first.endDate,
          isSleeper: false
        };
      } else {
        return {
          id: first.id,
          title: originTitle ?? '',
          geoCoordinates: first.type === 'Journey' ? first.originGeoCoordinates : first.geoCoordinates,
          type: first.type as 'Journey' | 'Stop',
          nights: first.nights ?? 0,
          timelinePosition: 'start' as const,
          startDate: first.startDate || undefined,
          endDate: first.endDate || undefined,
          isSleeper: false
        };
      }
    };
    const origin = getOrigin(first);

    // Create destination (end)
    const destinationTitle = last.type === 'Journey' ? last.destination : last.title;
    const getDestination = (last: AnyJourneyItemTransformed): MapOverviewItem => {
      if (last.type === 'Stay') {
        return {
          id: last.id,
          title: destinationTitle ?? '',
          geoCoordinates: last.geoCoordinates,
          type: 'Stay' as const,
          nights: last.nights,
          timelinePosition: 'end' as const,
          stayDates: getStayDates(destinationTitle ?? '', items),
          startDate: last.startDate,
          endDate: last.endDate,
          isSleeper: false
        };
      } else {
        return {
          id: last.id,
          title: destinationTitle ?? '',
          geoCoordinates: last.type === 'Journey' ? last.destinationGeoCoordinates : last.geoCoordinates,
          type: last.type as 'Journey' | 'Stop',
          nights: last.nights ?? 0,
          timelinePosition: 'end' as const,
          startDate: last.startDate || undefined,
          endDate: last.endDate || undefined,
          isSleeper: false
        };
      }
    };
    const destination = getDestination(last);

    // Create middle items
    const middle: MapOverviewItem[] = middleItems
      // stays, stops, and multi-night legs - multi-night legs excluded from map but included in timeline
      .filter(
        (item) =>
          (item.type === 'Stay' && item.geoCoordinates != null) ||
          (item.type === 'Stop' && item.geoCoordinates != null) ||
          (item.type === 'Journey' && item.nights && item.nights > 0)
      )

      .map((item) => {
        if (item.type === 'Stay') {
          return {
            id: item.id,
            title: item.title,
            geoCoordinates: item.geoCoordinates,
            type: 'Stay' as const,
            nights: item.nights,
            timelinePosition: 'middle' as const,
            stayDates: getStayDates(item.title, items),
            startDate: item.startDate,
            endDate: item.endDate,
            isSleeper: false
          };
        } else if (item.type === 'Stop') {
          return {
            id: item.id,
            title: item.title,
            geoCoordinates: item.geoCoordinates,
            type: 'Stop' as const,
            nights: 0,
            timelinePosition: 'middle' as const,
            startDate: item.startDate,
            endDate: item.endDate,
            isSleeper: false
          };
        } else {
          // multi-night or WALK Journey leg - only sleeper/overnight vehicle modes get the cabin title
          const isSleeper = isSleeperLeg(item.modeOfTransports);
          const title = getMiddleJourneyTitle(item.modeOfTransports, item.nights, item.origin);
          return {
            id: item.id,
            title,
            geoCoordinates: item.originGeoCoordinates,
            type: 'Journey' as const,
            nights: item.nights,
            timelinePosition: 'middle' as const,
            stayDates: getStayDates(title, items),
            startDate: item.startDate,
            endDate: item.endDate,
            isSleeper
          };
        }
      });

    const startSleeper: MapOverviewItem[] =
      first.type === 'Journey' && first.nights && first.nights > 0
        ? [
            {
              id: first.id,
              title: getMiddleJourneyTitle(first.modeOfTransports, first.nights, first.origin),
              geoCoordinates: first.originGeoCoordinates,
              type: 'Journey',
              nights: first.nights,
              timelinePosition: 'middle',
              stayDates: getStayDates(getMiddleJourneyTitle(first.modeOfTransports, first.nights, first.origin), items),
              startDate: first.startDate,
              endDate: first.endDate,
              isSleeper: isSleeperLeg(first.modeOfTransports)
            }
          ]
        : [];

    const endSleeper: MapOverviewItem[] =
      last.type === 'Journey' && last.nights && last.nights > 0
        ? [
            {
              id: last.id,
              title: getMiddleJourneyTitle(last.modeOfTransports, last.nights, last.origin),
              geoCoordinates: last.originGeoCoordinates,
              type: 'Journey',
              nights: last.nights,
              timelinePosition: 'middle',
              stayDates: getStayDates(getMiddleJourneyTitle(last.modeOfTransports, last.nights, last.origin), items),
              startDate: last.startDate,
              endDate: last.endDate,
              isSleeper: isSleeperLeg(last.modeOfTransports)
            }
          ]
        : [];

    const postTransformItems = [origin, ...startSleeper, ...middle, ...endSleeper, destination];
    return postTransformItems;
  } catch (_error) {
    return [];
  }
}

/**
 * Generates a GeoJSON FeatureCollection representing a bezier curve through the given points.
 *
 * @param points - Array of objects with geoCoordinates { latitude, longitude }
 * @returns GeoJSON FeatureCollection with a single bezierSpline curve feature
 */
type CurvePoint = {
  geoCoordinates: { latitude: number; longitude: number };
};

export function generateCurveGeoJson(points: CurvePoint[]) {
  if (!points || points.length < MAP_CONSTANTS.MIN_MARKERS_FOR_CURVE) return null;
  const lineData = points.map((p) => [p.geoCoordinates.longitude, p.geoCoordinates.latitude]);
  const line = lineString(lineData);
  const curve = bezierSpline(line, {
    resolution: MAP_CONSTANTS.CURVE_RESOLUTION,
    sharpness: MAP_CONSTANTS.CURVE_SHARPNESS
  });
  return {
    type: 'FeatureCollection',
    features: [curve]
  };
}

/**
 * Returns the bounding box and zoom level for the current map view.
 * @param map - Maplibre map instance
 * @returns [bbox, zoom]
 */
export function getMapBboxZoom(map: MapRef) {
  const bounds = map.getBounds();
  const { _sw, _ne } = bounds;
  const bbox = [_sw.lng, _sw.lat, _ne.lng, _ne.lat];
  const zoom = map.getZoom();
  return [bbox, zoom] as [BBox, number];
}

/**
 * Converts a CMSPlace to a GeoJSON Point feature.
 * @param place - strongly typed GraphQL query result
 * @returns GeoJSON Point feature or null
 */
export function parseObjectToGeoJSONPoint(place: Place['place']): Feature<Point, Place['place']> | null {
  if (!place || !place.location) return null;
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [place.location.longitude, place.location.latitude]
    },
    properties: {
      ...place
    }
  };
}

/**
 * Calculates clusters for a set of places using Supercluster and the current map view.
 * @param supercluster - MutableRefObject of a Supercluster instance
 * @param places - Array of CMSPlace objects
 * @param mapRef - MutableRefObject of a MapRef
 * @returns Array of cluster features
 */
export function calculateClusters(
  supercluster: RefObject<Supercluster>,
  places: Places['allPlaces'],
  mapRef: RefObject<MapRef | null>
) {
  const geoJsonPins = places
    .map((place) => parseObjectToGeoJSONPoint(place))
    .filter((pin): pin is Feature<Point, NonNullable<Place['place']>> => pin !== null && pin.properties !== null);
  supercluster.current?.load(geoJsonPins);
  if (!mapRef.current) return;
  const [bbox, zoom] = getMapBboxZoom(mapRef.current);
  const newClusters: Supercluster.PointFeature<GeoJsonProperties>[] = supercluster.current.getClusters(bbox, zoom);
  return newClusters;
}

// Helper function to safely format dates without year
export const safeFormatDate = (startDate?: string, endDate?: string): string => {
  if (
    !startDate ||
    !endDate ||
    Number.isNaN(new Date(startDate).getTime()) ||
    Number.isNaN(new Date(endDate).getTime())
  ) {
    return '';
  }
  try {
    return formatExactDate(startDate, endDate, undefined, false);
  } catch (_error) {
    return '';
  }
};

/**
 * Converts a PlaygroundPlace to CMS place - i.e. Place['place'].
 * Uses dummy values for missing fields as a temporary measure.
 */
export function convertStartEndPlaceToCmsPlace(
  startEndPlace: PlaygroundPlace,
  cmsPlaceName?: string | null
): Place['place'] {
  return {
    id: String(startEndPlace.id),
    placeId: startEndPlace.id,
    approved: null,
    placeName: cmsPlaceName || startEndPlace.n,
    location: {
      latitude: startEndPlace.geo.latitude,
      longitude: startEndPlace.geo.longitude
    },
    description: '',
    playgroundDescription: '',
    images: [],
    regions: [],
    country: startEndPlace.c
      ? {
          id: '',
          countryName: startEndPlace.c.n,
          countryId: ''
        }
      : {
          id: '',
          countryName: null,
          countryId: ''
        }
  };
}

export const formatDayRanges = (overviewItems: StayTransformed[], tripStartDate?: string) => {
  if (!tripStartDate) return pluralize(overviewItems[0].nights);
  return overviewItems.map((stay) => formatDayRange(stay, tripStartDate)).join(', ');
};

/**
 * Formats the day range for a stay.
 * @param overviewItem - The overview item to format the day range for
 * @param tripStartDate - The start date of the trip
 * @returns The formatted day range
 */
export const formatDayRange = (overviewItem: StayTransformed, tripStartDate?: string): string => {
  // If we have the trip start date, calculate the day numbers
  if (!tripStartDate) return '';

  const tripStart = new Date(tripStartDate);
  const stayStart = new Date(overviewItem.startDate);
  const stayEnd = new Date(overviewItem.endDate);

  // Calculate days from trip start (1-indexed)
  const startDay = Math.floor((stayStart.getTime() - tripStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const endDay = Math.floor((stayEnd.getTime() - tripStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  if (startDay === endDay) {
    return `Day ${startDay}`;
  }
  return `Day ${startDay}-${endDay}`;
};

/**
 * Processes and truncates place description text
 * Prioritizes AI suggestions over regular descriptions
 * @param aiSuggestions - AI-generated description (optional)
 * @param description - Regular place description (optional)
 * @returns Processed description text, truncated to 200 characters
 */
export const getProcessedDescription = (aiSuggestions?: string | null, description?: string | null): string => {
  // Prioritize AI suggestions if available
  const textToProcess =
    aiSuggestions && typeof aiSuggestions === 'string' && aiSuggestions.trim().length > 0
      ? aiSuggestions.trim()
      : description?.trim();

  if (!textToProcess) {
    return 'Sorry, no description was found for this place!';
  }

  const processed = textToProcess;

  if (processed.length <= 200) {
    return processed;
  }

  // Find the last space before 200 characters for clean truncation
  const truncated = processed.substring(0, 200);
  const lastSpaceIndex = truncated.lastIndexOf(' ');

  if (lastSpaceIndex > 0) {
    return `${truncated.substring(0, lastSpaceIndex).replace(/[.,!?;:]$/, '')}...`;
  }

  return `${truncated.replace(/[.,!?;:]$/, '')}...`;
};
